import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Search, X, Check, Filter } from 'lucide-react';
import { Vendor, VendorSelectorProps, VendorStatus, VendorType } from '../types/vendor';
import { allVendors } from './sharedVendorData';
import { sampleOrgUnits, flattenOrgUnits } from '../financial/sharedFinancialData';
import { Badge } from './ui/Badge';
// Derive org units from shared data
const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
// Helper: determine availability label for a vendor
function getAvailabilityLabel(vendor: Vendor): string {
  if (!vendor.orgUnitId) return 'Global';
  if (vendor.orgUnit) return vendor.orgUnit.name;
  const ou = allOrgUnits.find(o => o.id === vendor.orgUnitId);
  return ou ? ou.name : 'Org Unit';
}
export function VendorSelector({
  value,
  onChange,
  currentEntityId,
  currentSiteId,
  placeholder = 'Search vendors...',
  disabled = false,
  error
}: VendorSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [statusFilter, setStatusFilter] = useState<VendorStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<VendorType | null>(null);
  const [orgUnitFilter, setOrgUnitFilter] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Memoized filtered vendors for performance with large datasets
  const filteredVendors = useMemo(() => {
    return allVendors.filter(vendor => {
      // Exclude deleted vendors
      if (vendor.status === VendorStatus.Deleted) return false;
      // Apply status filter
      if (statusFilter && vendor.status !== statusFilter) return false;
      // Apply type filter
      if (typeFilter && vendor.type !== typeFilter) return false;
      // Apply org unit filter
      if (orgUnitFilter) {
        if (orgUnitFilter === 'global') {
          if (vendor.orgUnitId) return false;
        } else {
          if (vendor.orgUnitId !== orgUnitFilter) return false;
        }
      }
      // Check search query - includes code, name, and taxNumber
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return vendor.code.toLowerCase().includes(query) || vendor.name.toLowerCase().includes(query) || vendor.taxNumber && vendor.taxNumber.toLowerCase().includes(query);
      }
      return true;
    });
  }, [statusFilter, typeFilter, orgUnitFilter, searchQuery]);
  const selectedVendor = value ? allVendors.find(v => v.id === value) : null;
  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredVendors]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && !inputRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredVendors.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredVendors[highlightedIndex]) {
          handleSelect(filteredVendors[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };
  const handleSelect = (vendor: Vendor) => {
    onChange(vendor.id);
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };
  const getStatusBadge = (vendor: Vendor) => {
    switch (vendor.status) {
      case VendorStatus.Active:
        return <Badge variant="default" data-id="element-23112">Active</Badge>;
      case VendorStatus.Draft:
        return <Badge variant="outline" data-id="element-23113">Draft</Badge>;
      case VendorStatus.PendingReview:
        return <Badge variant="secondary" data-id="element-23114">Pending Review</Badge>;
      case VendorStatus.Inactive:
        return <Badge variant="outline" data-id="element-23115">Inactive</Badge>;
      case VendorStatus.Suspended:
        return <Badge variant="destructive" data-id="element-23116">Suspended</Badge>;
      default:
        return <Badge variant="outline" data-id="element-23117">{vendor.status}</Badge>;
    }
  };
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return <>
        {parts.map((part, i) => part.toLowerCase() === query.toLowerCase() ? <mark key={i} className="bg-blue-100 text-blue-900 font-medium" data-id="element-23118">
              {part}
            </mark> : <span key={i} data-id="element-23119">{part}</span>)}
      </>;
  };
  // Memoized status counts
  const statusCounts = useMemo(() => ({
    all: allVendors.filter(v => v.status !== VendorStatus.Deleted).length,
    active: allVendors.filter(v => v.status === VendorStatus.Active).length,
    draft: allVendors.filter(v => v.status === VendorStatus.Draft).length,
    pending: allVendors.filter(v => v.status === VendorStatus.PendingReview).length,
    inactive: allVendors.filter(v => v.status === VendorStatus.Inactive).length
  }), []);
  // Unique org units used by vendors
  const vendorOrgUnits = useMemo(() => {
    const orgUnitIds = new Set(allVendors.filter(v => v.orgUnitId).map(v => v.orgUnitId));
    return allOrgUnits.filter(ou => orgUnitIds.has(ou.id));
  }, []);
  // Limit displayed results for performance
  const displayedVendors = filteredVendors.slice(0, 100);
  const hasMoreResults = filteredVendors.length > 100;
  return <div className="relative" data-id="element-23120">
      <div className="relative" data-id="element-23121">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-23122">
          <Search className="w-4 h-4" data-id="element-23123" />
        </div>
        <input ref={inputRef} type="text" value={isOpen ? searchQuery : selectedVendor ? `${selectedVendor.code} - ${selectedVendor.name}` : ''} onChange={e => {
        setSearchQuery(e.target.value);
        if (!isOpen) setIsOpen(true);
      }} onFocus={() => {
        setIsOpen(true);
        if (selectedVendor) setSearchQuery('');
      }} onKeyDown={handleKeyDown} placeholder={placeholder} disabled={disabled} className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'} ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`} aria-label="Vendor selector" aria-expanded={isOpen} aria-controls="vendor-dropdown" role="combobox" data-id="element-23124" />
        {selectedVendor && !disabled && <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Clear selection" data-id="element-23125">
            <X className="w-4 h-4" data-id="element-23126" />
          </button>}
      </div>
      {error && <p className="mt-1 text-sm text-red-600" data-id="element-23127">{error}</p>}

      {isOpen && <div ref={dropdownRef} id="vendor-dropdown" className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col" role="listbox" data-id="element-23128">
          {/* Filters */}
          <div className="border-b border-gray-200 p-3 bg-gray-50" data-id="element-23129">
            <div className="flex items-center gap-2 mb-2" data-id="element-23130">
              <Filter className="w-3.5 h-3.5 text-gray-500" data-id="element-23131" />
              <span className="text-xs font-medium text-gray-600 uppercase" data-id="element-23132">
                Filter by Status
              </span>
            </div>
            <div className="flex gap-2 flex-wrap mb-3" data-id="element-23133">
              <button type="button" onClick={() => {
            setStatusFilter(null);
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${statusFilter === null ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-23134">
                All ({statusCounts.all})
              </button>
              <button type="button" onClick={() => setStatusFilter(VendorStatus.Active)} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${statusFilter === VendorStatus.Active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-23135">
                Active ({statusCounts.active})
              </button>
              <button type="button" onClick={() => setStatusFilter(VendorStatus.Draft)} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${statusFilter === VendorStatus.Draft ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-23136">
                Draft ({statusCounts.draft})
              </button>
              <button type="button" onClick={() => setStatusFilter(VendorStatus.PendingReview)} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${statusFilter === VendorStatus.PendingReview ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-23137">
                Pending ({statusCounts.pending})
              </button>
              <button type="button" onClick={() => setStatusFilter(VendorStatus.Inactive)} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${statusFilter === VendorStatus.Inactive ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-23138">
                Inactive ({statusCounts.inactive})
              </button>
            </div>

            {/* Org Unit Filter */}
            <div data-id="element-23139">
              <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-23140">
                Organisation Unit:
              </label>
              <select value={orgUnitFilter || ''} onChange={e => setOrgUnitFilter(e.target.value || null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-23141">
                <option value="" data-id="element-23142">All Org Units</option>
                <option value="global" data-id="element-23143">Global (No Org Unit)</option>
                {vendorOrgUnits.map(ou => <option key={ou.id} value={ou.id} data-id="element-23144">
                    {ou.code} - {ou.name}
                  </option>)}
              </select>
            </div>
          </div>

          {/* Results count and search hint */}
          {filteredVendors.length > 0 && <div className="px-4 py-2 bg-blue-50 border-b border-blue-100" data-id="element-23145">
              <p className="text-xs text-blue-700" data-id="element-23146">
                {filteredVendors.length === 1 ? '1 vendor found' : `${filteredVendors.length} vendors found`}
                {hasMoreResults && <span className="ml-1 text-blue-600" data-id="element-23147">
                    (showing first 100 - use search to narrow results)
                  </span>}
              </p>
            </div>}

          {/* Vendor List */}
          <div className="flex-1 overflow-auto" data-id="element-23148">
            {displayedVendors.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-23149">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" data-id="element-23150" />
                <p className="text-sm font-medium" data-id="element-23151">No vendors found</p>
                <p className="text-xs mt-1" data-id="element-23152">
                  {searchQuery || statusFilter || typeFilter || orgUnitFilter ? 'Try adjusting your search or filters' : 'No vendors available'}
                </p>
              </div> : <div className="py-1" data-id="element-23153">
                {displayedVendors.map((vendor, index) => <button key={vendor.id} type="button" onClick={() => handleSelect(vendor)} onMouseEnter={() => setHighlightedIndex(index)} className={`w-full px-4 py-3 text-left transition-colors ${index === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'} ${value === vendor.id ? 'bg-blue-50' : ''}`} role="option" aria-selected={value === vendor.id} data-id="element-23154">
                    <div className="flex items-start justify-between gap-3" data-id="element-23155">
                      <div className="flex-1 min-w-0" data-id="element-23156">
                        <div className="flex items-center gap-2 mb-1" data-id="element-23157">
                          <span className="font-mono text-sm font-medium text-gray-900" data-id="element-23158">
                            {highlightMatch(vendor.code, searchQuery)}
                          </span>
                          {getStatusBadge(vendor)}
                        </div>
                        <p className="text-sm text-gray-700 truncate" data-id="element-23159">
                          {highlightMatch(vendor.name, searchQuery)}
                        </p>
                        <div className="flex items-center gap-3 mt-1" data-id="element-23160">
                          {vendor.taxNumber && <p className="text-xs text-gray-500" data-id="element-23161">
                              Tax:{' '}
                              {highlightMatch(vendor.taxNumber, searchQuery)}
                            </p>}
                          <p className="text-xs text-gray-500" data-id="element-23162">
                            {getAvailabilityLabel(vendor)}
                          </p>
                          {vendor.country && <p className="text-xs text-gray-500" data-id="element-23163">
                              {vendor.country.name}
                            </p>}
                        </div>
                      </div>
                      {value === vendor.id && <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-23164" />}
                    </div>
                  </button>)}
              </div>}
          </div>
        </div>}
    </div>;
}